import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteDto, RouteSaveDto } from "../../models/route/route";
import { deleteRoute, getRoutes, saveRoute } from "../../services/routeService";
import { showApiError } from "../framework/formUtils";
import { MuiEditableDataGridActionIconButton } from "../framework/editableDataGrid/muiEditableDataGridActionCellItem";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import {
    FieldError,
    MuiEditableDataGrid,
} from "../framework/editableDataGrid/muiEditableDataGrid";
import { useAppDispatch } from "../../framework/customStore";
import {
    clearConfirmation,
    setConfirmation,
    showSuccessMessage,
} from "../../models/store/storeActions";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import { IApiError } from "../../services/baseService";
import { MuiSwitch } from "../framework/muiSwitch";
import CheckIcon from "@mui/icons-material/Check";

export function SettingsRouteList() {
    const [items, setItems] = useState<RouteDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [showInactive, setShowInactive] = useState(false);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const fetchItems = useCallback((signal?: AbortSignal) => {
        setLoading(true);
        getRoutes(!showInactive, signal)
            .then(setItems)
            .catch(showApiError)
            .finally(() => setLoading(false));
    }, [showInactive]);

    useEffect(() => fetchItems(), [fetchItems]);

    const handleSubmit = async(values: RouteSaveDto) => {
        return new Promise<RouteDto>((resolve, reject) => {
            saveRoute(values)
                .then((res) => {
                    dispatch(showSuccessMessage(t("common.saveSuccess")));
                    resolve(res);
                    fetchItems();
                })
                .catch((error: IApiError) => {
                    reject(
                        new Error(
                            error?.status === 409
                                ? t("common.saveConflict")
                                : t("common.saveFailed")
                        )
                    );
                });
        });
    };

    const handleDelete = useCallback(
        (id: string) =>
            dispatch(
                setConfirmation(
                    ConfirmationDialogType.Warning,
                    t("common.warning"),
                    t("route.confirmDeleteRoute"),
                    () => {
                        dispatch(clearConfirmation());

                        deleteRoute(id)
                            .then(() => {
                                dispatch(
                                    showSuccessMessage(
                                        t("common.deleteSuccess")
                                    )
                                );
                                fetchItems();
                            })
                            .catch(showApiError);
                    },
                    () => dispatch(clearConfirmation())
                )
            ),
        []
    );

    const gridDef: GridColDef<RouteDto>[] = useMemo(
        () => [
            {
                headerName: t("common.name"),
                hideable: false,
                field: "name",
                editable: true,
                flex: 1,
                type: "string",
            },
            {
                headerName: t("common.description"),
                hideable: true,
                field: "comment",
                editable: true,
                type: "string",
                flex: 2,
            },
            {
                headerName: t("common.inUse"),
                hideable: true,
                field: "isActive",
                width: 100,
                editable: true,
                type: "boolean",
                renderCell: (params: GridRenderCellParams) =>
                    params.value ? (
                        <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                            <CheckIcon color="success" fontSize="small" />
                        </div>
                    ) : (
                        null
                    ),
            },
            {
                headerName: "",
                hideable: false,
                field: "actions",
                type: "actions",
                width: 80,
                renderCell: (params: GridRenderCellParams<RouteDto>) => [
                    <MuiEditableDataGridActionIconButton
                        key="edit"
                        isEdit
                        color="primary"
                        title={t("common.edit")}
                        fieldToFocus="name"
                        params={params}
                    >
                        <EditIcon fontSize="small" />
                    </MuiEditableDataGridActionIconButton>,
                    <MuiEditableDataGridActionIconButton
                        key="delete"
                        id={`settings-route-delete-${params.id}`}
                        color="error"
                        title={t("common.delete")}
                        onClick={() => handleDelete(params.id.toString())}
                        params={params}
                    >
                        <DeleteIcon fontSize="small" />
                    </MuiEditableDataGridActionIconButton>,
                ],
            },
        ],
        [handleDelete]
    );

    const handleAddRow = (): RouteSaveDto => ({
        id: null,
        rowId: null,
        name: "",
        comment: null,
        isActive: true,
    });

    const validateRow = useCallback((row: RouteSaveDto) => {
        const errors: FieldError[] = [];

        if (!row.name || !row.name.replace(/ /g, "")) {
            errors.push({
                field: "name",
                error: t("validation.fieldIsRequired", {
                    field: t("common.name"),
                }),
            });
        }

        return errors;
    }, []);

    return (
        <>
            <LoadingIndicator loading={loading} />
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%", marginBottom: 8 }}>
                <MuiSwitch
                    id="settings-routes-showInactive"
                    label={t("route.showInactiveRoutes")}
                    checked={showInactive}
                    onChange={(_, checked) => setShowInactive(checked)}
                />
            </div>
            <MuiEditableDataGrid<RouteDto>
                title={t("route.routes")}
                persistStateKey="SettingsRouteList"
                columns={gridDef}
                rows={items}
                setRows={setItems}
                handleAddRow={handleAddRow}
                handleSubmit={handleSubmit}
                validateRow={validateRow}
                fieldToFocus="name"
                hideFooter
                defaultSortModel={[{ field: "name", sort: "asc" }]}
            />
        </>
    );
}
